import { Chip, Fade, IconButton, Typography } from '@material-ui/core'
import RefreshIcon from '@material-ui/icons/Refresh'
import React from 'react'

import NoDataImage from '../../../assets/images/No-Data.png'
import { PromotionCode } from '../../../graphql/promotions'
import { getTranslate, lang } from '../../../localization'
import useStyles from './styles/PromotionCodesTableMobile.styles'

const dateFormatter = new Intl.DateTimeFormat(
  lang === 'fa' ? 'fa-IR' : 'en-GB',
  { dateStyle: 'short', timeStyle: 'short' }
)

interface Props {
  data: PromotionCode[]
  loading: boolean
  reloadData: () => void
}

/**
 * Mobile layout for the voucher audit.
 *
 * Duplicates LAYOUT, never behaviour — it renders the same rows from the same query the
 * desktop table does, and owns no fetching, filtering or paging of its own. Twin branches
 * that must stay in step are where copy-paste defects live (conventions section 2).
 *
 * Two columns instead of four: the code and its state. Booking and consumption time move
 * into a secondary line under the code rather than being dropped — on an audit surface
 * "which booking" is the answer being looked for, so hiding it on small screens would
 * remove the point of the view.
 */
export const PromotionCodesTableMobile: React.FC<Props> = ({
  data,
  loading,
  reloadData
}) => {
  const classes = useStyles()

  return (
    <div>
      <div className={classes.tableHead}>
        <Typography variant="body1">{getTranslate('Code')}</Typography>
        <div className={classes.refreshCell}>
          <IconButton
            size="small"
            onClick={reloadData}
            aria-label={getTranslate('Refresh')}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </div>
      </div>

      {loading &&
        Array(5)
          .fill('')
          .map((_, index) => (
            <div key={index} className={classes.tableRow}>
              <div className={classes.skeletonBlock} />
            </div>
          ))}

      {!loading &&
        data.map((code) => (
          <Fade in timeout={600} key={code.code}>
            <div className={classes.tableRow}>
              <div className={classes.cell}>
                <Typography variant="body1">
                  {/* One <bdi> per token — see PromotionCodeRow. */}
                  <bdi>{code.code}</bdi>
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {code.bookingId ? <bdi>{code.bookingId}</bdi> : '—'}
                  {code.consumedAt
                    ? ` · ${dateFormatter.format(new Date(code.consumedAt))}`
                    : ''}
                </Typography>
              </div>
              <Chip
                size="small"
                color={
                  code.state === 'consumed'
                    ? 'secondary'
                    : code.state === 'reserved'
                    ? 'primary'
                    : 'default'
                }
                label={getTranslate(code.state)}
              />
            </div>
          </Fade>
        ))}

      {!loading && data.length === 0 && (
        <div className={classes.noDataImageContainer}>
          <img src={NoDataImage} alt={getTranslate('No vouchers issued yet')} />
        </div>
      )}
    </div>
  )
}

export default PromotionCodesTableMobile
